import { useState } from 'react';
import { MISSION_BY_ID } from '../../data/missions';
import { canPlayerSwapMission } from '../../domain/phaseStatus';
import { missionSwapCost } from '../../domain/chipUtils';
import { HUMAN_PLAYER_INDEX } from '../../domain/types';
import { useGameStore } from '../../store/gameStore';
import { HumanRoundHistorySection } from './HumanRoundHistorySection';
import { MissionCard } from './MissionCard';
import { MissionSwapConfirmDialog } from './MissionSwapConfirmDialog';

const UNAVAILABLE_PHASES = new Set([
  'setup',
  'game_over',
  'mission_draft',
  'mission_select',
  'round_roll',
]);

function getUnusedMissionSwapHint(
  phase: string,
  draftTurn: number,
  canSwap: boolean,
  alreadySwapped: boolean,
  betAmount: number,
): string {
  if (phase !== 'draft') {
    return '미사용 미션';
  }
  if (draftTurn > 3) {
    return '미사용 미션 (4턴에는 교체 불가)';
  }
  if (alreadySwapped) {
    return '미사용 미션 · 이번 라운드 교체 완료';
  }
  if (canSwap) {
    const cost = missionSwapCost(draftTurn);
    return `미사용 미션 · 탭하여 교체 확인 (비용 ${cost}점)`;
  }
  const cost = missionSwapCost(draftTurn);
  return `미사용 미션 · 교체 불가 (베팅 ${cost + 1}점 이상 필요, 현재 ${betAmount}점)`;
}

export function MissionsMenuDrawer() {
  const game = useGameStore((s) => s.game);
  const dispatch = useGameStore((s) => s.dispatch);
  const missionsMenuOpen = useGameStore((s) => s.missionsMenuOpen);
  const setMissionsMenuOpen = useGameStore((s) => s.setMissionsMenuOpen);
  const { phase, players, draftTurn, activePlayerIndex } = game;
  const human = players[HUMAN_PLAYER_INDEX];
  const [pendingSwapMissionId, setPendingSwapMissionId] = useState<string | null>(null);

  if (!missionsMenuOpen) return null;

  const unavailable = UNAVAILABLE_PHASES.has(phase) || !human || human.missionHand.length === 0;

  const selectedMission = human?.selectedMissionId
    ? MISSION_BY_ID[human.selectedMissionId]
    : null;
  const showCurrentRound =
    selectedMission !== null &&
    selectedMission !== undefined &&
    phase !== 'mission_select' &&
    phase !== 'round_roll';
  const reservedHand =
    human?.missionHand.filter((id) => id !== human.selectedMissionId) ?? [];
  const canSwap =
    phase === 'draft' &&
    activePlayerIndex === HUMAN_PLAYER_INDEX &&
    human !== undefined &&
    canPlayerSwapMission(game, HUMAN_PLAYER_INDEX);

  const pendingNewMission = pendingSwapMissionId
    ? MISSION_BY_ID[pendingSwapMissionId]
    : null;

  const handleConfirmSwap = () => {
    if (!pendingSwapMissionId) return;
    dispatch({
      type: 'SWAP_MISSION',
      playerIndex: HUMAN_PLAYER_INDEX,
      newMissionId: pendingSwapMissionId,
    });
    setPendingSwapMissionId(null);
  };

  const handleClose = () => {
    setPendingSwapMissionId(null);
    setMissionsMenuOpen(false);
  };

  return (
    <>
      <div
        className="pointer-events-auto fixed inset-0 z-20 flex flex-col justify-end bg-black/50"
        role="presentation"
        onClick={handleClose}
      >
        <div
          className="overlay-panel mx-4 mb-4 max-h-[70vh] overflow-y-auto rounded-2xl p-4"
          role="dialog"
          aria-label="미션 및 기록"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-amber-100">미션 및 기록</h2>
            <button
              type="button"
              className="overlay-button-ghost min-h-[44px] px-3 py-2"
              onClick={handleClose}
            >
              닫기
            </button>
          </div>

          {unavailable ? (
            <p className="text-sm text-white/65">
              지금은 미션 메뉴를 사용할 수 없습니다. 미션 선택·드래프트는 플레이 화면에서
              진행하세요.
            </p>
          ) : (
            <>
              <HumanRoundHistorySection className="mb-4" />

              {showCurrentRound && selectedMission && (
                <section className="mb-4">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-200/80">
                    이번 라운드 미션
                  </h3>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    <MissionCard mission={selectedMission} variant="selected" />
                  </div>
                </section>
              )}

              {reservedHand.length > 0 && (
                <section>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-200/80">
                    {getUnusedMissionSwapHint(
                      phase,
                      draftTurn,
                      canSwap,
                      human.missionSwappedThisRound,
                      human.betAmount,
                    )}
                  </h3>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {reservedHand.map((id) => {
                      const mission = MISSION_BY_ID[id];
                      if (!mission) return null;
                      const isSwapTarget =
                        phase === 'draft' &&
                        activePlayerIndex === HUMAN_PLAYER_INDEX;

                      return (
                        <MissionCard
                          key={id}
                          mission={mission}
                          variant="compact"
                          onClick={
                            isSwapTarget && canSwap
                              ? () => setPendingSwapMissionId(id)
                              : undefined
                          }
                          disabled={isSwapTarget && !canSwap}
                          actionHint={isSwapTarget && canSwap ? '선택' : undefined}
                        />
                      );
                    })}
                  </div>
                </section>
              )}

              {game.humanRoundHistory.length === 0 &&
                !showCurrentRound &&
                reservedHand.length === 0 && (
                  <p className="text-sm text-white/65">표시할 미션 정보가 없습니다.</p>
                )}
            </>
          )}
        </div>
      </div>

      {selectedMission && pendingNewMission && human && (
        <MissionSwapConfirmDialog
          open={pendingSwapMissionId !== null}
          draftTurn={draftTurn}
          currentMission={selectedMission}
          newMission={pendingNewMission}
          betAmount={human.betAmount}
          onConfirm={handleConfirmSwap}
          onCancel={() => setPendingSwapMissionId(null)}
        />
      )}
    </>
  );
}
